'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import { Padding, Typography } from '@musinsa/mds';
import { useDialog } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { curatorQueries } from '..';
import { SearchProductInput } from '../product';
import { SelectableVirtualizedLikedProductList } from '../product/components/SelectableVirtualizedLikedProductList';
import { SelectableVirtualizedSearchedProductList } from '../product/components/SelectableVirtualizedSearchedProductList';
import { SNOWPLOW_SCHEME } from '../snowplow';
import { SelectedWithButton } from './SelectedWithButton';
import type { ShopCurationEditFormData } from './ShopGoodsCurationEdit';
import type { CurationFormGoodsItem } from './type';

interface Props {
  defaultCheckedProducts: CurationFormGoodsItem[];
  onSubmit: () => void;
  /** Amplitude `type` 필드 (상품 큐레이션 플로우는 `goods`) */
  analyticsCurationType?: 'goods' | 'snap';
}

export const SelectGoods = ({
  defaultCheckedProducts,
  onSubmit,
  analyticsCurationType = 'goods',
}: Props) => {
  const { service } = useDeviceInfo();
  const { data: userInfo } = useQuery(curatorQueries.profile.userInfo);
  const curatorId = userInfo?.snsTitle ?? '';
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const { control, setValue } = useFormContext<ShopCurationEditFormData>();
  const checkedProducts =
    useWatch({
      control,
      name: 'selectedProducts',
    }) ??
    defaultCheckedProducts ??
    [];

  const [keyword, setKeyword] = useState('');
  const isSearchMode = keyword.length > 0;

  const [isListEmpty, setIsListEmpty] = useState(false);
  const handleEmptyStateChange = useCallback((isEmpty: boolean) => {
    setIsListEmpty(isEmpty);
  }, []);

  const syncCheckedProducts = useCallback(
    (products: CurationFormGoodsItem[]) => {
      setValue('selectedProducts', products, {
        shouldDirty: true,
        shouldValidate: false,
      });
    },
    [setValue],
  );

  const onClickCheckbox = (product: CurationFormGoodsItem) => {
    const isAlreadyChecked = checkedProducts.some((item) => item.goodsNo === product.goodsNo);

    const newProducts = isAlreadyChecked
      ? checkedProducts.filter((item) => item.goodsNo !== product.goodsNo)
      : [...checkedProducts, product];

    if (newProducts.length > 100) {
      dialog.open({
        title: '최대 100개의 상품을 선택할 수 있습니다.',
        confirmButtonText: '확인',
        onConfirm: () => dialog.close(),
      });
      return;
    }

    if (!isAlreadyChecked && service === SERVICE.MUSINSA && curatorId) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'goods_select',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        name: isSearchMode ? 'search' : 'like',
        section_name: 'curator_curation_create',
        type: analyticsCurationType,
      });
    }

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_select_product',
          section_name: isSearchMode ? 'SearchResultProducts' : 'LikedProducts',
          section_title: isSearchMode ? '검색 결과 상품' : '좋아요한 상품',
          button_id: isAlreadyChecked ? 'remove_product' : 'add_product',
          button_name: isAlreadyChecked ? '상품 제외하기' : '상품 추가하기',
          item_no_list: product.goodsNo,
          position: -1,
        },
      });
    }

    syncCheckedProducts(newProducts);
  };

  useEffect(() => {
    setIsListEmpty(false);
  }, [isSearchMode]);

  useEffect(() => {
    if (isSearchMode) {
      queryClient.invalidateQueries({
        queryKey: curatorQueries.products.liked._def,
      });
      return;
    }
    queryClient.invalidateQueries({
      queryKey: curatorQueries.products.search._def,
    });
  }, [isSearchMode, queryClient]);

  useEffect(() => {
    if (!isSearchMode || !keyword.trim()) {
      return;
    }
    if (service !== SERVICE.MUSINSA || !curatorId) {
      return;
    }
    amplitudeSendEvent({
      action_name: 'click',
      button_name: 'goods_search',
      component_name: 'search_button',
      event_name: 'click_search_button',
      from: getScreenName(),
      id: curatorId,
      section_name: 'curator_curation_create',
      type: analyticsCurationType,
    });
  }, [analyticsCurationType, curatorId, isSearchMode, keyword, service]);

  return (
    <>
      <div className={'flex w-full flex-col grow pb-[72px]!'}>
        <SearchProductInput
          keyword={keyword}
          placeholder="큐레이션할 상품을 검색해 보세요."
          onChangeKeyword={(value) => setKeyword(value)}
        />
        {isSearchMode ? (
          <>
            {!isListEmpty && (
              <Padding x="4" y="3">
                <Typography variant="title18pxMed">검색 결과 상품</Typography>
              </Padding>
            )}
            <SelectableVirtualizedSearchedProductList
              keyword={keyword}
              checkedProducts={checkedProducts}
              onClickCheckbox={onClickCheckbox}
              curatorId={curatorId}
              onEmptyStateChange={handleEmptyStateChange}
            />
          </>
        ) : (
          <SelectableVirtualizedLikedProductList
            checkedProducts={checkedProducts}
            onClickCheckbox={onClickCheckbox}
            curatorId={curatorId}
            onEmptyStateChange={handleEmptyStateChange}
          />
        )}
      </div>
      {!(isListEmpty && checkedProducts.length === 0) && (
        <SelectedWithButton
          selectedProducts={checkedProducts}
          analyticsCurationType={analyticsCurationType}
          onUnselect={(goodsNo) => {
            syncCheckedProducts(checkedProducts.filter((item) => item.goodsNo !== goodsNo));
          }}
          onDragEnd={syncCheckedProducts}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};
